import React, { Suspense, useEffect, useState } from "react";

import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.css";
import { toast } from "react-toastify";
import { ErrorMessage } from "../../Utils/ErrorMsg/ErrorMsg";
import { stripHTML } from "./StripHtml";

const FroalaEditor = React.lazy(() => import("react-froala-wysiwyg"));

export const FroalaTextEditor = (props) => {
  const { description, setDescription, max_character_count } = props;

  const [isInitialized, setIsInitialized] = useState(false);
  const [isToastShown, setIsToastShown] = useState(false);
  const handleModelChange = (model) => {
    setDescription(model);

    const plainText = stripHTML(model);
    const currentCharCount = plainText.length;
    const hasInvalidTags = /<img|<figure/i.test(model);

    if (
      hasInvalidTags &&
      currentCharCount > max_character_count &&
      !isToastShown
    ) {
      toast.error(
        `Character limit exceeded! Max ${max_character_count} characters. Images are not allowed.`,
        { position: "top-center", autoClose: 6000, hideProgressBar: true },
      );
      setIsToastShown(true);
    } else if (hasInvalidTags && !isToastShown) {
      toast.error(ErrorMessage.ImageError, {
        position: "top-center",
        autoClose: 6000,
        hideProgressBar: true,
      });
      setIsToastShown(true);
    } else if (currentCharCount > max_character_count && !isToastShown) {
      toast.error(
        `Character limit exceeded! Max ${max_character_count} characters.`,
        { position: "top-center", autoClose: 6000, hideProgressBar: true },
      );
      setIsToastShown(true);
    } else if (
      currentCharCount <= max_character_count &&
      !hasInvalidTags &&
      isToastShown
    ) {
      setIsToastShown(false);
    }
  };

  useEffect(() => {
    async function initPlugins() {
      await import("froala-editor/js/plugins.pkgd.min.js");
      setIsInitialized(true);
    }

    if (!isInitialized) {
      initPlugins();
    }
  }, [isInitialized]);

  return (
    <>
      {isInitialized && (
        <Suspense fallback={<p>Loading editor...</p>}>
          <FroalaEditor
            tag="textarea"
            model={description || ""}
            onModelChange={handleModelChange}
            config={{
              key: process.env.REACT_APP_FROALA_KEY,
              pastePlain: false,
              wordPasteModal: false,
              autofocus: false,
              quickInsertEnabled: false,

              charCounterCount: true,
              charCounterMax: max_character_count,

              pastePlain: false,
              pasteDeniedTags: ["img", "figure"],
              pasteDeniedAttrs: ["style"],

              htmlAllowedAttrs: ["href", "src", "alt", "title", "class", "id"],

              // Ensure consistent font after save/reload
              fontFamilyDefaultSelection: "Montserrat, sans-serif",

              events: {
                "paste.beforeCleanup": function (html) {
                  // Remove style tags
                  let text = html.replace(
                    /<style[^>]*>[\s\S]*?<\/style>/gi,
                    "",
                  );

                  // Convert HTML to plain text safely
                  const div = document.createElement("div");
                  div.innerHTML = text;

                  text = div.textContent || div.innerText || "";

                  // Remove Word-specific garbage text
                  text = text.replace(
                    /Normal\s+\d+\s+false\s+false\s+false\s+false\s+EN-US\s+X-NONE\s+X-NONE/gi,
                    "",
                  );

                  // Remove extra blank lines
                  text = text.replace(/\n\s*\n/g, "\n").trim();

                  const current = stripHTML(this.html.get());
                  const remaining = max_character_count - current.length;

                  if (remaining <= 0) {
                    if (!isToastShown) {
                      toast.error(
                        "Character limit reached. Additional content cannot be pasted.",
                        {
                          position: "top-center",
                          autoClose: 4000,
                        },
                      );
                      setIsToastShown(true);
                    }
                    return "";
                  }

                  return text.substring(0, remaining);
                },

                keydown: function (e) {
                  const current = stripHTML(this.html.get());

                  if (
                    current.length >= max_character_count &&
                    e.key.length === 1 &&
                    !e.ctrlKey &&
                    !e.metaKey
                  ) {
                    e.preventDefault();
                  }
                },
              },

              toolbarButtons: {
                moreText: {
                  buttons: [
                    "bold",
                    "italic",
                    "underline",
                    "strikeThrough",
                    "subscript",
                    "superscript",
                    // "fontFamily",
                    // "fontSize",
                    // "clearFormatting",
                  ],
                },
                moreParagraph: {
                  buttons: [
                    "alignLeft",
                    "alignCenter",
                    "alignRight",
                    "alignJustify",
                    "formatUL",
                    "paragraphFormat",
                    "lineHeight",
                    "outdent",
                    "indent",
                    "quote",
                  ],
                },
                moreRich: {
                  buttons: ["insertLink", "fontAwesome", "embedly"],
                },
                moreMisc: {
                  buttons: [
                    "undo",
                    "redo",
                    "fullscreen",
                    "selectAll",
                    "html",
                    "help",
                  ],
                  align: "right",
                  buttonsVisible: 2,
                },
              },
            }}
          />
        </Suspense>
      )}
    </>
  );
};
